import { ListingForm } from 'components/shared/TableListing';
import { Nullable } from 'global';
import { UserProfile } from '@trnsact/trnsact-shared-types';
import { CreateBPFTemplateInput, Template } from '../api';
import { Recipient, StagesFormValues, TemplateConstructorMode, TemplateFormValues } from '../types';
import { initialStagesConstructorFormValue, stagesConstructorFormRow } from '../constants';
import { formatSnakeToCamelCaseLabel } from 'utils/formatters';

class StagesMapper {
  processStageFields(stage: StagesFormValues): CreateBPFTemplateInput['stages'][number]['stageFields'] {
    return [
      {
        field: 'checklist',
        value: stage.checklist.map(({ name, isRequired }) => ({ name, isRequired, value: false })),
      },
    ];
  }

  processFormValuesToCreateOrUpdateDto(input: {
    accountId: string;
    mode: TemplateConstructorMode;
    templateValues: TemplateFormValues;
    stagesValues: ListingForm<StagesFormValues>;
  }): CreateBPFTemplateInput {
    const { stagesValues, mode, accountId, templateValues } = input;

    return {
      accountId,
      name: templateValues.templateName,
      isActive: templateValues.isActive,
      departmentId: templateValues.businessProcess,
      applyRules: {
        paymentType: templateValues.applyRules.paymentType,
      },
      stages: stagesValues.bodyFields.map((stage, index) => ({
        ordinal: index,
        name: stage.name,
        completionRules: {},
        stageFields: this.processStageFields(stage),
        notificationRules: {
          notificationEvents: ['STAGE_COMPLETED'],
          userProfileRecipients: stage.recipients
            .filter(r => {
              return !!r.userProfileId;
            })
            .map(r => {
              return { userProfileId: r.userProfileId };
            }),
          roleRecipients: stage.recipients
            .filter(r => {
              return !!r.role;
            })
            .map(r => {
              return { role: r.role };
            }),
        },
        ...(stage.id && mode === 'edit' ? { id: stage.id } : {}),
      })),
    };
  }

  processTemplateToFormValues(template: Template): TemplateFormValues {
    return {
      ...initialStagesConstructorFormValue,
      isActive: template.isActive,
      templateName: template.name,
      businessProcess: template.department.id,
      ...(Object.keys(template.applyRules).length
        ? {
            applyRules: template.applyRules,
            ...initialStagesConstructorFormValue.applyRules,
          }
        : { applyRules: initialStagesConstructorFormValue.applyRules }),
    };
  }

  processTemplateToConstructorFormValues(
    template: Nullable<Template>,
    relatedUsers?: Map<string, UserProfile>
  ): StagesFormValues[] {
    if (!template) return [{ ...stagesConstructorFormRow }];

    return template.stages.map(({ businessProcessFlowStageId, name, stageFields, notificationRules }) => {
      const recipients: Recipient[] = [];
      if (notificationRules && Array.isArray(notificationRules)) {
        notificationRules.forEach(nr => {
          if (nr.userProfileRecipients && Array.isArray(nr.userProfileRecipients)) {
            nr.userProfileRecipients.forEach((upr: any) => {
              recipients.push({
                name: relatedUsers ? relatedUsers.get(upr.userProfileId)?.fullName || 'Unknown User' : 'Unknown User',
                userProfileId: upr.userProfileId,
              });
            });
          }
          if (nr.roleRecipients && Array.isArray(nr.roleRecipients)) {
            nr.roleRecipients.forEach((rr: any) => {
              recipients.push({
                name: formatSnakeToCamelCaseLabel(rr.role),
                role: rr.role,
              });
            });
          }
        });
      }
      return {
        name,
        id: businessProcessFlowStageId,
        checklist: (Array.isArray(stageFields) ? stageFields : []).reduce<StagesFormValues['checklist']>(
          (acc, stageField) => {
            if (stageField.field !== 'checklist') return acc;

            stageField.value.forEach(fieldData => {
              acc.push({ ...fieldData, value: fieldData.value ?? false });
            });

            return acc;
          },
          []
        ),
        recipients,
      };
    });
  }
}

export const stagesMapper = new StagesMapper();
